import React, { useEffect, useState } from 'react'
import { fmt } from '../api.js'
import { portal } from '../portalApi.js'
import NoteDrawer from '../components/NoteDrawer.jsx'
import { CompanyPicker, NeedCompany, costGet, useCompany, useMeta, useToast } from './CostCommon.jsx'

// 원가관리 · 기초데이터(배부기준).
//
// 세 덩어리가 한 화면에 있다. 셋 다 "원장과 명세가 왜 다른가"를 다루기 때문이다.
//   1) 계정별원장 월 실적 — 넣으면 배부대상이 정해진다
//   2) 라이선스 선급금 — 연초에 한 번 지급하고 생산할 때마다 상각한다.
//      그 달 원장에 매입이 없는 게 정상이고, 대신 지급액을 넘겨 배부하면 안 된다.
//   3) 미계상 원가 — 명세엔 있는데 원장엔 없는 나머지. 세금계산서·지급을 놓친 자리다.

export default function CostBasis({ year, onMonthChange, currentUserId, autoOpenNoteId, onAutoOpened }) {
  const [company, setCompany] = useCompany()
  const [meta] = useMeta()
  const [data, setData] = useState(null)
  const [lic, setLic] = useState(null)
  const [ub, setUb] = useState(null)
  const [edits, setEdits] = useState({})
  const [licEdits, setLicEdits] = useState({})
  const [ubEdits, setUbEdits] = useState({})
  const [accEdits, setAccEdits] = useState({})
  const [newLic, setNewLic] = useState('')
  const [openLic, setOpenLic] = useState('')
  const [showAcc, setShowAcc] = useState(false)
  const [err, setErr] = useState('')
  const [notify, toast] = useToast()

  const load = () => {
    setErr('')
    costGet('/allocation-basis', { year, company })
      .then((r) => { setData(r); setEdits({}) }).catch((e) => setErr(e.message))
    costGet('/license', { year, company })
      .then((r) => { setLic(r); setLicEdits({}) }).catch(() => setLic(null))
    costGet('/unbooked', { year, company })
      .then((r) => { setUb(r); setUbEdits({}) }).catch(() => setUb(null))
  }
  useEffect(() => { load() }, [year, company])

  const save = async () => {
    const list = Object.entries(edits).map(([k, v]) => {
      const [month, field] = k.split('|')
      return { company, month, field, amount: Number(v) || 0 }
    })
    if (!list.length) return
    try {
      await portal.post('/api/cost/ledger', list)
      notify(`${list.length}건 저장 완료`)
      load()
    } catch (e) { setErr(e.message) }
  }

  const saveAccounts = async () => {
    const list = (data?.fields || []).map((f) => ({
      field: f.field, name: f.label,
      code: accEdits[f.field]?.code ?? f.code,
      used: accEdits[f.field]?.used ?? f.used,
    }))
    try {
      await portal.post('/api/cost/accounts', list)
      notify('원가 항목 설정 저장 — 새로고침합니다')
      setTimeout(() => window.location.reload(), 700)
    } catch (e) { setErr(e.message) }
  }

  // ── 라이선스 ──────────────────────────────────────────────
  const lv = (r, k, dflt) => licEdits[r.name]?.[k] ?? (r[k] ?? dflt)
  const setLv = (r, k, v) => setLicEdits({ ...licEdits, [r.name]: { ...licEdits[r.name], [k]: v } })

  const saveLicense = async () => {
    const rows = Object.entries(licEdits).map(([name, e]) => {
      const base = lic.rows.find((x) => x.name === name) || {}
      return {
        name,
        prepaid: Number(e.prepaid ?? base.prepaid) || 0,
        unit_rate: Number(e.unitRate ?? base.unitRate) || 0,
        sale_rate: Number(e.saleRate ?? base.saleRate) || 0,
        paid_on: e.paidOn ?? base.paidOn ?? '',
        final_month: e.finalMonth ?? base.finalMonth ?? '',
        memo: e.memo ?? base.memo ?? '',
      }
    })
    if (!rows.length) return
    try {
      await portal.post('/api/cost/license', { company, year, rows })
      notify(`라이선스 계약 ${rows.length}건 저장`)
      load()
    } catch (e) { setErr(e.message) }
  }

  const addLicense = () => {
    const name = newLic.trim()
    if (!name) return
    setLic({ ...lic, rows: [...(lic?.rows || []), {
      name, registered: false, prepaid: 0, unitRate: 0, saleRate: 0,
      paidOn: '', finalMonth: '', memo: '', used: 0, balance: null, qty: 0, months: [],
    }] })
    setLicEdits({ ...licEdits, [name]: { prepaid: '', unitRate: '' } })
    setNewLic('')
  }

  // ── 미계상 ────────────────────────────────────────────────
  const ubKey = (r) => `${r.month}|${r.field}`
  const ubVal = (r, k) => ubEdits[ubKey(r)]?.[k] ?? r[k]
  const setUbVal = (r, k, v) =>
    setUbEdits({ ...ubEdits, [ubKey(r)]: { ...ubEdits[ubKey(r)], [k]: v } })

  const saveUnbooked = async () => {
    const list = Object.keys(ubEdits).map((k) => {
      const r = ub.rows.find((x) => ubKey(x) === k)
      return { month: r.month, field: r.field, gap: r.gap,
        status: ubVal(r, 'status'), reason: ubVal(r, 'reason'), memo: ubVal(r, 'memo') }
    })
    if (!list.length) return
    try {
      await portal.post('/api/cost/unbooked', { company, rows: list })
      notify(`${list.length}건 기록 완료`)
      load()
    } catch (e) { setErr(e.message) }
  }

  if (err && !data) return <div className="error-box">{err}</div>
  if (!data) return <div className="muted">불러오는 중…</div>
  const fields = data.fields.filter((f) => f.used || f.code)
  const reasons = meta?.unbookedReasons || []
  const statuses = meta?.unbookedStatuses || []
  const missing = (ub?.rows || []).filter((r) => r.needsAction)
  const amort = (ub?.rows || []).filter((r) => !r.needsAction)

  return (
    <>
      <div style={{ position: 'relative' }}>
        <h2>{year}년 기초데이터 — 계정별원장 월 실적</h2>
        <NoteDrawer target="basis" screenKey="cost_basis"
          context={{ year }} contextLabel={`${year}년`}
          onNavigate={(ctx) => ctx?.year && onMonthChange?.(`${ctx.year}-01`)}
          autoOpenNoteId={autoOpenNoteId} onAutoOpened={onAutoOpened}
          currentUserId={currentUserId} />
      </div>
      <div className="row" style={{ marginBottom: 12 }}>
        <CompanyPicker value={company} onChange={setCompany} meta={meta} />
        <button className="btn secondary" onClick={() => setShowAcc(!showAcc)}>
          원가 항목·계정코드 {showAcc ? '닫기' : '설정'}
        </button>
      </div>
      {err && <div className="error-box">{err}</div>}
      {!company && <NeedCompany>원장·라이선스 계약은 사업부별로 잡습니다. 통합 화면에서는 합계만 보이고 입력은 막혀 있습니다.</NeedCompany>}

      {showAcc && (
        <div className="panel">
          <h3>원가 항목</h3>
          <div className="hint">
            공통경비·인건비는 제조원가명세서 요약에 자리는 있지만 계정이 아직 안 정해져 꺼져 있습니다.
            계정코드를 넣고 켜면 배부기준·명세·분개에 함께 나옵니다.
          </div>
          <table style={{ maxWidth: 560 }}>
            <thead><tr><th className="l">항목</th><th>계정코드</th><th>사용</th></tr></thead>
            <tbody>
              {data.fields.map((f) => (
                <tr key={f.field}>
                  <td className="l">{f.label}</td>
                  <td>
                    <input style={{ width: 90 }} value={accEdits[f.field]?.code ?? f.code}
                      onChange={(e) => setAccEdits({ ...accEdits, [f.field]: { code: e.target.value, used: accEdits[f.field]?.used ?? f.used } })} />
                  </td>
                  <td className="c">
                    <input type="checkbox" checked={accEdits[f.field]?.used ?? f.used}
                      onChange={(e) => setAccEdits({ ...accEdits, [f.field]: { code: accEdits[f.field]?.code ?? f.code, used: e.target.checked } })} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button className="btn" style={{ marginTop: 10 }} onClick={saveAccounts}
            disabled={!Object.keys(accEdits).length}>항목 설정 저장</button>
        </div>
      )}

      <div className="hint">
        <b>원장 금액</b>을 넣으면 <b>배부대상 = 원장 금액 − 명세 직접귀속액</b>이 자동 계산되어 그 달 품목에 직접비 비율로 배부됩니다.
        원장 금액이 0이면 그 계정은 배부 없이 직접귀속액만 반영됩니다.
        <br />· <b>원·부재료비</b> = 전기 재고액 + 당기 매입액 − 당기 재고액 (당기 소비액)
        · <b>외주가공비·소모품비</b> = 세금계산서 마감 후 당기 발생액
        · <b>직접경비</b>의 직접귀속에는 아래 라이선스 상각분이 포함됩니다
      </div>
      <div className="panel">
        <table>
          <thead>
            <tr>
              <th rowSpan={2}>월</th>
              {fields.map((f) => (
                <th key={f.field} colSpan={3}>{f.label}{f.code ? ` (${f.code})` : ''}</th>
              ))}
            </tr>
            <tr>
              {fields.map((f) => (
                <React.Fragment key={f.field}>
                  <th>원장 금액</th><th>직접귀속</th><th>배부대상</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.month}>
                <td className="c">{r.month}</td>
                {fields.map((f) => {
                  const a = r.accounts[f.field]
                  const key = `${r.month}|${f.field}`
                  const val = edits[key] ?? String(a.ledger || '')
                  const ledger = Number(edits[key] ?? a.ledger) || 0
                  const pool = ledger === 0 ? 0 : ledger - a.direct
                  return (
                    <React.Fragment key={f.field}>
                      <td>
                        <input value={val} disabled={!company}
                          onChange={(e) => setEdits({ ...edits, [key]: e.target.value })} />
                      </td>
                      <td>{fmt(a.direct)}</td>
                      <td className={pool < 0 ? 'warn' : ''}>{fmt(pool)}</td>
                    </React.Fragment>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="row" style={{ marginTop: 12 }}>
          <button className="btn" onClick={save} disabled={!company || !Object.keys(edits).length}>
            변경사항 저장 ({Object.keys(edits).length})
          </button>
        </div>
      </div>

      {lic && (
        <div className="panel">
          <h3>라이선스 선급금</h3>
          <div className="hint" style={{ marginTop: 0 }}>
            연초에 한 번 지급하고(차: 선급금) 생산할 때마다 개당 얼마씩 상각해 원가로 보냅니다(차: 직접경비 / 대: 선급금).
            그래서 그 달 원장에 매입이 없는 게 정상이고, 대신 <b>지급액을 넘겨 배부하면 안 됩니다</b>.
            잔액이 마르면 그 뒤로는 배부가 0이 되고, <b>마지막 생산 달</b>을 찍으면 남은 잔액을 그 달에 전부 배부합니다.
          </div>
          <table>
            <thead>
              <tr>
                <th className="l">라이선스</th><th>선지급액</th><th>개당 단가</th><th>판매가 비율</th>
                <th>지급일</th><th>마지막 달</th><th>생산량</th><th>누적 배부</th><th>잔액</th>
                <th>남은 수량</th><th>상태</th><th />
              </tr>
            </thead>
            <tbody>
              {lic.rows.length === 0 && <tr><td colSpan={12} className="c muted">라이선스 품목이 없습니다</td></tr>}
              {lic.rows.map((r) => (
                <React.Fragment key={r.name}>
                  <tr>
                    <td className="l"><b>{r.name}</b></td>
                    <td><input style={{ width: 110 }} disabled={!company}
                      value={lv(r, 'prepaid', 0)} onChange={(e) => setLv(r, 'prepaid', e.target.value)} /></td>
                    <td><input style={{ width: 70 }} disabled={!company}
                      value={lv(r, 'unitRate', 0)} onChange={(e) => setLv(r, 'unitRate', e.target.value)} /></td>
                    <td><input style={{ width: 60 }} disabled={!company}
                      value={lv(r, 'saleRate', 0)} onChange={(e) => setLv(r, 'saleRate', e.target.value)} /></td>
                    <td><input type="date" style={{ width: 130 }} disabled={!company}
                      value={lv(r, 'paidOn', '')} onChange={(e) => setLv(r, 'paidOn', e.target.value)} /></td>
                    <td><input type="month" style={{ width: 120 }} disabled={!company}
                      value={lv(r, 'finalMonth', '')} onChange={(e) => setLv(r, 'finalMonth', e.target.value)} /></td>
                    <td>{fmt(r.qty)}</td>
                    <td>{fmt(r.used)}</td>
                    <td className={r.balance !== null && r.balance <= 0 ? 'warn' : ''}>
                      {r.balance === null ? '-' : fmt(r.balance)}
                    </td>
                    <td>{r.remainQty === null || r.remainQty === undefined ? '-' : fmt(r.remainQty)}</td>
                    <td className="c">
                      {!r.registered ? <span className="muted">미등록</span>
                        : r.exhausted ? <span className="warn">소진</span>
                          : r.over ? <span className="warn">부족</span>
                            : <span className="ok">정상</span>}
                    </td>
                    <td className="c">
                      <button className="btn secondary"
                        onClick={() => setOpenLic(openLic === r.name ? '' : r.name)}>월별</button>
                    </td>
                  </tr>
                  {openLic === r.name && (
                    <tr>
                      <td colSpan={12}>
                        <table style={{ maxWidth: 760 }}>
                          <thead><tr><th>월</th><th>생산량</th><th>계약상 예정액</th><th>실제 배부</th><th>배부비율</th><th>잔액</th></tr></thead>
                          <tbody>
                            {r.months.filter((x) => x.qty || x.alloc).map((x) => (
                              <tr key={x.month}>
                                <td className="c">{x.month}{x.isFinal && <span className="warn"> 마지막</span>}</td>
                                <td>{fmt(x.qty)}</td>
                                <td>{fmt(x.planned)}</td>
                                <td className={x.factor < 1 ? 'warn' : ''}>{fmt(x.alloc)}</td>
                                <td>{(x.factor * 100).toFixed(0)}%</td>
                                <td>{x.balance === null ? '-' : fmt(x.balance)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          <div className="row" style={{ marginTop: 12 }}>
            <input placeholder="새 라이선스 이름" style={{ width: 160 }} disabled={!company}
              value={newLic} onChange={(e) => setNewLic(e.target.value)} />
            <button className="btn secondary" onClick={addLicense} disabled={!company || !newLic.trim()}>행 추가</button>
            <button className="btn" onClick={saveLicense} disabled={!company || !Object.keys(licEdits).length}>
              계약 저장 ({Object.keys(licEdits).length})
            </button>
          </div>
          <div className="hint">
            개당 단가와 판매가 비율은 하나만 채우면 됩니다 (단가가 있으면 단가가 이깁니다).
            <b> 배부비율 100% 미만</b>이면 잔액이 모자라 예정보다 적게 붙은 달이고,
            <b> 100% 초과</b>는 마지막 달로 찍어 잔액을 전부 턴 달입니다.
            라이선스 이름은 품목의 라이선스 구분과 같은 값이어야 연결됩니다.
          </div>
        </div>
      )}

      {ub && (missing.length > 0 || amort.length > 0) && (
        <div className="panel">
          <h3>
            원장에 없는 원가
            {missing.length > 0 && (
              <span className="warn"> — 미계상 {ub.totals.missingCount}건 {fmt(ub.totals.missingAmount)}원</span>
            )}
            {amort.length > 0 && (
              <span className="muted"> · 라이선스 상각 {fmt(ub.totals.amortAmount)}원</span>
            )}
          </h3>
          <div className="hint" style={{ marginTop: 0 }}>
            명세에는 잡혀 있는데 계정별원장에는 그만큼이 없는 금액입니다. 둘로 갈립니다.
            <br />· <b>선급금 상각</b> — 라이선스처럼 미리 낸 돈을 헐어 쓰는 것. 원장에 매입이 없는 게 정상이고, 상각분개만 올리면 됩니다.
            <br />· <b>미계상</b> — 세금계산서 수취나 지급이 아직 안 된 것. 원래는 선급금·미지급금으로 잡혀 있어야 합니다.
          </div>
          <table>
            <thead>
              <tr>
                <th>월</th><th className="l">원가 항목</th><th>구분</th>
                <th>원장</th><th>명세 직접귀속</th><th>상각분</th><th>미계상</th>
                <th>받을 계정</th><th>상태</th><th className="l">메모</th><th className="l">제안 분개</th>
              </tr>
            </thead>
            <tbody>
              {ub.rows.map((r) => (
                <tr key={ubKey(r)} style={r.needsAction ? undefined : { opacity: 0.72 }}>
                  <td className="c">{r.month}</td>
                  <td className="l">{r.label}{r.account ? ` (${r.account})` : ''}</td>
                  <td className="c">
                    {r.kind}
                    {r.changed && <div className="warn" style={{ fontSize: 11 }}>금액 바뀜</div>}
                  </td>
                  <td>{fmt(r.ledger)}</td>
                  <td>{fmt(r.direct)}</td>
                  <td>{r.amortAmt ? fmt(r.amortAmt) : '-'}</td>
                  <td className={r.missingAmt ? 'warn' : ''}>{r.missingAmt ? <b>{fmt(r.missingAmt)}</b> : '-'}</td>
                  <td>
                    {r.needsAction ? (
                      <select value={ubVal(r, 'reason')} disabled={!company}
                        onChange={(e) => setUbVal(r, 'reason', e.target.value)}>
                        {reasons.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
                      </select>
                    ) : <span className="muted">선급금</span>}
                  </td>
                  <td>
                    {r.needsAction ? (
                      <select value={ubVal(r, 'status')} disabled={!company}
                        onChange={(e) => setUbVal(r, 'status', e.target.value)}>
                        {statuses.map((x) => <option key={x}>{x}</option>)}
                      </select>
                    ) : <span className="muted">-</span>}
                  </td>
                  <td>
                    {r.needsAction ? (
                      <input style={{ width: 150 }} value={ubVal(r, 'memo')} disabled={!company}
                        placeholder="전표번호·담당자" onChange={(e) => setUbVal(r, 'memo', e.target.value)} />
                    ) : <span className="muted">-</span>}
                  </td>
                  <td className="l" style={{ fontSize: 12 }}>
                    {r.entries.map((e, i) => {
                      const cr = e.kind === '선급금 상각'
                        ? e.credit
                        : { ...e.credit, account_name: (reasons.find((x) => x.key === ubVal(r, 'reason')) || {}).label || e.credit.account_name }
                      return (
                        <div key={i} style={i ? { marginTop: 4 } : undefined}>
                          차) {e.debit.account_name} {fmt(e.amount)}<br />
                          대) {cr.account_name} {fmt(e.amount)}
                        </div>
                      )
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="row" style={{ marginTop: 12 }}>
            <button className="btn" onClick={saveUnbooked} disabled={!company || !Object.keys(ubEdits).length}>
              확인 결과 저장 ({Object.keys(ubEdits).length})
            </button>
          </div>
          <div className="hint">
            {reasons.map((x) => <div key={x.key}><b>{x.label}</b>{x.code ? ` (${x.code})` : ''} — {x.desc}</div>)}
          </div>
          <div className="hint warn-hint">
            <b>금액 바뀜</b>은 확인한 뒤에 명세나 원장이 달라졌다는 뜻입니다 — 처리완료로 두지 말고 다시 보세요.
          </div>
        </div>
      )}
      {ub && ub.rows.length === 0 && (
        <div className="panel">
          <h3>원장에 없는 원가</h3>
          <div className="hint" style={{ marginTop: 0 }}>
            명세 직접귀속액이 원장을 넘는 달이 없습니다 — 회계처리가 다 따라왔다는 뜻입니다.
          </div>
        </div>
      )}
      {toast}
    </>
  )
}
